"use client";

import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { DESKTOP_Z_INDEX, ISLAND_LAYOUT } from "../desktopUI.constants";
import { Glyph, type GlyphName } from "./Glyph";
const BAR_GAP_PX = 8;
const BAR_HEIGHT_PX = 36;
const VIEWPORT_EDGE_PX = 8;
type SelectionAnchorRect = SelectionBarProps["anchor"];
const clamp = ({
  value,
  min,
  max
}: {
  value: number;
  min: number;
  max: number;
}) => Math.min(Math.max(value, min), Math.max(min, max));
export function placeSelectionBar({
  anchor,
  barWidth,
  viewport
}: {
  anchor: SelectionAnchorRect;
  barWidth: number;
  viewport: {
    width: number;
    height: number;
  };
}) {
  const left = clamp({
    value: anchor.left + anchor.width / 2 - barWidth / 2,
    min: VIEWPORT_EDGE_PX,
    max: viewport.width - VIEWPORT_EDGE_PX - barWidth
  });
  const lowestTop = viewport.height - VIEWPORT_EDGE_PX - BAR_HEIGHT_PX;
  const aboveTop = anchor.top - BAR_GAP_PX - BAR_HEIGHT_PX;
  const belowTop = anchor.top + anchor.height + BAR_GAP_PX;
  if (aboveTop >= ISLAND_LAYOUT.CLEARANCE) return {
    left,
    top: aboveTop
  };
  if (belowTop <= lowestTop) return {
    left,
    top: belowTop
  };
  return {
    left,
    top: clamp({
      value: anchor.top + BAR_GAP_PX,
      min: ISLAND_LAYOUT.CLEARANCE,
      max: lowestTop
    })
  };
}
export interface SelectionBarAction {
  label: string;
  glyph?: GlyphName;
  shortcut?: string;
  onSelect: () => void;
}
export interface SelectionBarProps {
  anchor: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  actions: SelectionBarAction[];
  label: string;
  className?: string;
  isInline?: boolean;
}
export function SelectionBar({
  anchor,
  actions,
  label,
  className,
  isInline = false
}: SelectionBarProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = useState(0);
  useLayoutEffect(() => {
    if (!isInline) setBarWidth(barRef.current?.offsetWidth ?? 0);
  }, [isInline, actions.length]);
  const position = isInline || typeof window === "undefined" ? null : placeSelectionBar({
    anchor,
    barWidth,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    }
  });
  const bar = <div ref={barRef} role="toolbar" aria-label={label} data-selection-bar onMouseDown={event => event.preventDefault()} style={position ? {
    zIndex: DESKTOP_Z_INDEX.OVERLAY,
    ...position
  } : undefined} className={cn("flex w-fit items-center gap-0.5 rounded-full bg-ink p-1 text-surface shadow-float", !isInline && "fixed motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 duration-fast ease-emphasized", className)}>
            {actions.map(action => <button key={action.label} type="button" onClick={action.onSelect} className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-callout font-medium text-surface transition-colors hover:bg-surface/[0.14] focus-visible:bg-surface/[0.14] focus-visible:outline-none">
                    {action.glyph && <Glyph name={action.glyph} className="h-3.5 w-3.5" />}
                    {action.label}
                    {action.shortcut && <span className="font-mono text-footnote text-surface/60">
                            {action.shortcut}
                        </span>}
                </button>)}
        </div>;
  if (isInline || typeof document === "undefined") return bar;
  return createPortal(bar, document.body);
}